/**
 * Midpoint Media Client View — site-wide content defaults.
 * Editable in Client View Studio → Pages → content JSON.
 */
import {
  MIDPOINT_MEDIA_SECTION_IMAGES,
  midpointMediaDefaultImages,
} from "./midpointMediaSectionImages.js";
import {
  mergeMidpointMediaCaseStudies,
  midpointMediaCaseStudyDefaults,
} from "./midpointMediaCaseStudies.js";

export const MIDPOINT_MEDIA_MARKETING_HOME_LAYOUT = "midpoint-media-marketing";
export const MIDPOINT_MEDIA_SLUG = "midpoint-media";

export const MIDPOINT_MEDIA_HERO_BADGES = [
  "Signal Intelligence",
  "Data + AI Strategy",
  "Map-Driven Client Views",
];

export { midpointMediaDefaultImages };

export const midpointMediaSiteDefaults = {
  homeLayout: MIDPOINT_MEDIA_MARKETING_HOME_LAYOUT,
  centralMessage:
    "Midpoint transforms location, customer, and market data into focused Client Views that help teams understand where opportunity is emerging, what activity matters, and what action to take next.",
  navLinks: [
    { label: "Home", href: "/" },
    { label: "Platform", href: "/community-intelligence" },
    { label: "Client Views", href: "/client-views" },
    { label: "Consulting", href: "/consulting" },
    { label: "Contact", href: "/contact" },
    { label: "Sign In", href: "/sign-in" },
  ],
  clientPortal: {
    kicker: "Client account",
    title: "Sign in to your account",
    intro:
      "Access project billing, estimates, invoices, and support for products Midpoint Media maintains on your behalf.",
    submitLabel: "Sign in",
    helpText: "Need access or having trouble signing in?",
    contactLabel: "Contact us",
    expectKicker: "After you sign in",
    expectTitle: "What you can manage",
    expectIntro:
      "Your client portal is the single place to review project financials and request help for active engagements.",
    footnote:
      "Access is limited to clients with an active Midpoint Media account. New clients receive credentials after onboarding.",
    seoTitle: "Client Sign In — Midpoint Media",
    seoDescription:
      "Sign in to review project costs, estimates, invoices, and open support tickets with Midpoint Media.",
    features: [
      {
        title: "Project costs",
        copy: "Review cost summaries and billing context for active and completed project work.",
      },
      {
        title: "Estimates",
        copy: "View proposed scope, pricing, and estimate status for upcoming or in-flight work.",
      },
      {
        title: "Invoices",
        copy: "Access issued invoices, payment status, and historical billing records.",
      },
      {
        title: "Support tickets",
        copy: "Create and track support requests for products and platforms we maintain for you.",
      },
    ],
  },
  hero: {
    kicker: "Signal Intelligence Platform",
    title: "See the Signals Behind Every Market",
    subtitle:
      "Midpoint transforms location, customer, and market data into focused Client Views that help teams understand where opportunity is emerging, what activity matters, and what action to take next.",
    badges: MIDPOINT_MEDIA_HERO_BADGES,
    imageUrl: MIDPOINT_MEDIA_SECTION_IMAGES.hero.url,
    imageAlt: MIDPOINT_MEDIA_SECTION_IMAGES.hero.alt,
    primaryCta: { label: "Explore the Platform", href: "/community-intelligence" },
  },
  platformPositioning: {
    title: "A Signal Intelligence Platform Built Around Client Views",
    body:
      "Signals are everywhere: customer inquiries, property movement, community activity, CRM updates, market shifts, campaign performance, and operational events. Midpoint brings those signals together and turns them into focused Client Views that make complex activity easier to understand and act on.",
    imageUrl: MIDPOINT_MEDIA_SECTION_IMAGES.platform.url,
    imageAlt: MIDPOINT_MEDIA_SECTION_IMAGES.platform.alt,
    cards: [
      {
        title: "Capture the Signals",
        copy:
          "Bring together location data, CRM activity, market records, customer behavior, and operational events from multiple sources.",
      },
      {
        title: "Map the Context",
        copy:
          "Organize signals around places, communities, properties, accounts, opportunities, and business workflows.",
      },
      {
        title: "Act with Clarity",
        copy:
          "Give teams a focused operating picture so they can prioritize leads, understand market movement, and take the next best action.",
      },
    ],
  },
  clientViewsHome: {
    title: "Client Views Turn Signals into Focused Products",
    body:
      "A Client View is a focused experience built on the Midpoint platform for a specific market, client, community, or business model. Each Client View can combine public-facing pages, CRM intake, map-based search, lead routing, analytics, client portals, and operational workflows.",
    imageUrl: MIDPOINT_MEDIA_SECTION_IMAGES.clientViews.url,
    imageAlt: MIDPOINT_MEDIA_SECTION_IMAGES.clientViews.alt,
    includes: [
      "Branded landing pages",
      "Map-based discovery",
      "CRM lead capture",
      "Opportunity tracking",
      "Client portal access",
      "Market dashboards",
      "Data imports and enrichment",
      "Analytics and reporting",
      "Custom workflows",
    ],
    cta: { label: "Explore Client Views", href: "/client-views" },
  },
  golfHomes: {
    title: "Golf Homes: The First Client View",
    description:
      "Golf Homes is our first Client View: a real estate and community intelligence experience focused on golf course living. It demonstrates how Midpoint can combine location data, property records, CRM workflows, and market signals into a focused vertical product.",
    body:
      "Golf Homes is the first Client View built on the Midpoint platform. It uses map-driven search, golf community data, property information, CRM workflows, and market intelligence to help buyers, sellers, realtors, advertisers, and partners understand opportunities around golf communities.",
    imageUrl: MIDPOINT_MEDIA_SECTION_IMAGES.golfHomes.url,
    imageAlt: MIDPOINT_MEDIA_SECTION_IMAGES.golfHomes.alt,
    caseStudyHref: "/golf-homes",
  },
  consulting: {
    kicker: "Consulting & Business Operations",
    title: "Consulting That Connects Strategy to Execution",
    tagline: "Build better systems. Strengthen your team. Keep work moving.",
    intro:
      "Midpoint helps organizations plan and deliver technology projects, improve daily operations, and build the teams that support growth.",
    summary:
      "We help with technology, project delivery, and day-to-day operations. Browse one topic at a time, then tell us what you need.",
    imageUrl: MIDPOINT_MEDIA_SECTION_IMAGES.consulting.url,
    imageAlt: MIDPOINT_MEDIA_SECTION_IMAGES.consulting.alt,
    primaryCta: { label: "Discuss Your Needs", href: "/contact" },
    secondaryCta: { label: "See how we can help", href: "#consulting-topics" },
    capabilities: [
      "Enterprise architecture",
      "Full-stack application development",
      "Mapping and location intelligence",
      "Data engineering and analytics",
      "AI and workflow automation",
      "Project management",
      "Software testing and quality assurance",
      "Recruitment and HR support",
      "Office management and administrative support",
      "Call center setup and operations",
    ],
    sections: [
      {
        id: "technology",
        tabLabel: "Technology",
        title: "Technology & Data",
        summary: "Architecture, software, maps, data, and practical AI — shaped around how your business actually runs.",
        cards: [
          {
            title: "Enterprise Architecture",
            benefit:
              "Align your systems, investments, and technical roadmap with the way your business needs to operate.",
            items: [
              "System modernization and platform architecture",
              "Cloud and integration strategy",
              "Technical roadmaps",
              "Vendor evaluation and architecture governance",
            ],
          },
          {
            title: "Full-Stack Application Development",
            benefit:
              "Build websites, portals, and applications that simplify work and improve the customer experience.",
            items: [
              "Websites, portals, and customer-facing applications",
              "APIs and admin tools for internal teams",
              "Database design for the workflows the product needs",
              "Reliable application deployment and hosting setup",
            ],
          },
          {
            title: "Mapping & Location Intelligence",
            benefit:
              "Turn location and property data into maps and insights that support better decisions.",
            items: [
              "Map-based applications for properties, places, and service areas",
              "Property and parcel mapping",
              "Geospatial dashboards and spatial analysis",
              "Location data models for community and market intelligence",
            ],
          },
          {
            title: "Data Engineering and Analytics",
            benefit:
              "Connect scattered data sources and turn them into reliable reporting your team can use.",
            items: [
              "Pipelines that connect operational systems and warehouses",
              "Source-to-warehouse integration, including Salesforce",
              "Dashboards and reporting teams can trust",
              "Ongoing analytics workflows for business questions",
            ],
          },
          {
            title: "AI and Workflow Automation",
            benefit:
              "Reduce repetitive work and make information easier to find with practical AI tools and workflow automation.",
            items: [
              "Practical AI tools for repetitive operational work",
              "Document intelligence and project intake",
              "AI search across company documents and knowledge",
              "Workflow automation that routes work to the right people",
            ],
          },
        ],
      },
      {
        id: "delivery",
        tabLabel: "Delivery",
        title: "Project Delivery & Quality",
        summary: "Keep work coordinated and software ready for release, with clear ownership and fewer surprises.",
        cards: [
          {
            title: "Project Management",
            benefit:
              "Turn business priorities into coordinated execution. We organize scope, schedules, responsibilities, and communication so teams can move forward with clear ownership and fewer surprises.",
            items: [
              "Project planning, milestones, and scheduling",
              "Team, stakeholder, and vendor coordination",
              "Budget, risk, and issue tracking",
              "Progress reporting and delivery handoffs",
            ],
          },
          {
            title: "Software Testing & Quality Assurance",
            benefit:
              "Find problems before your customers do. We test applications against real business workflows, document defects clearly, and help your team assess readiness for release.",
            items: [
              "Functional and regression testing",
              "User acceptance testing coordination",
              "Browser, mobile, and usability checks",
              "Defect reporting, retesting, and release validation",
            ],
          },
        ],
      },
      {
        id: "operations",
        tabLabel: "Operations",
        title: "Business Operations & People",
        summary: "Hiring, administration, and customer operations that keep the business moving.",
        cards: [
          {
            title: "Recruitment & HR Support",
            benefit:
              "Find the right people and keep hiring organized. We support recruitment, onboarding, and day-to-day HR administration so your team can focus on building the business.",
            items: [
              "Job descriptions and candidate sourcing",
              "Candidate screening and interview coordination",
              "Employee onboarding and offboarding coordination",
              "HR documentation and employee records support",
            ],
          },
          {
            title: "Office Management & Administrative Support",
            benefit:
              "Keep daily operations organized and moving. We help manage schedules, documentation, vendors, and recurring tasks so important work gets the attention it needs.",
            items: [
              "Executive assistance and calendar coordination",
              "Vendor coordination and purchasing support",
              "Document management and data entry",
              "Standard operating procedures and task tracking",
            ],
          },
          {
            title: "Call Center Setup & Operations",
            benefit:
              "Build a customer support or outreach operation with the people, processes, and tools to run it, including bilingual English–Spanish delivery. Setup and ongoing operations are separate engagement options, with different responsibilities and pricing.",
            items: [
              "Staffing plans, recruitment, and agent onboarding",
              "Phone systems, CRM setup, and call workflows",
              "Scripts, training, and quality monitoring",
              "Scheduling, supervision, and performance reporting",
            ],
          },
        ],
      },
    ],
    engagements: {
      tabLabel: "How we work",
      title: "Support That Fits the Work",
      summary: "Choose the level of help that matches the work — a defined project, ongoing support, or a managed function.",
      options: [
        {
          title: "Project-Based Services",
          body: "Complete a defined initiative with an agreed scope, clear deliverables, and a delivery schedule.",
        },
        {
          title: "Ongoing Team Support",
          body: "Add recurring technical, administrative, or project support as your workload grows.",
        },
        {
          title: "Managed Operations",
          body: "Engage Midpoint to coordinate an agreed business function, with defined responsibilities, service expectations, and performance reporting.",
        },
      ],
    },
    credibility: {
      title: "Experience Behind the Work",
      body: "Engagements are led by practitioners with more than 20 years of enterprise technology experience across architecture, software delivery, data platforms, and operations. That experience belongs to the people doing the work.",
      caseStudyLabel: "See it in a shipped product: Golf Homes case study",
      caseStudyHref: "/golf-homes",
    },
    closing: {
      title: "What Does Your Business Need Next?",
      body: "Tell us what you are building, improving, or struggling to keep up with. We’ll help define the scope and the right approach.",
      cta: { label: "Discuss Your Needs", href: "/contact" },
    },
  },
  differentiator: {
    title: "Not Just Maps. Not Just Dashboards. A Clearer Operating Picture.",
    body:
      "Maps show where things happen. Dashboards show what happened. Midpoint connects both into Client Views that explain where activity is happening, why it matters, and what teams should do next.",
    tagline:
      "Signals are the inputs. Maps provide context. Client Views create the product experience.",
  },
  homeCta: {
    title: "Build Your First Client View",
    body:
      "Have a market, community, service area, or customer workflow that needs a clearer operating picture? Midpoint can help turn your scattered signals into a focused Client View.",
    primaryCta: { label: "Start a Conversation", href: "/contact" },
    secondaryCta: { label: "Explore Golf Homes", href: "/v/golf-homes" },
  },
  platform: {
    title: "Signal Intelligence Platform",
    positioning:
      "Midpoint is a Signal Intelligence Platform that transforms location data, CRM activity, market movement, and customer behavior into map-driven Client Views.",
    description:
      "Signals are everywhere in your business. Midpoint brings them together, maps the context, and delivers focused Client Views so teams can see opportunity, understand activity, and act with confidence.",
    imageUrl: MIDPOINT_MEDIA_SECTION_IMAGES.platform.url,
    imageAlt: MIDPOINT_MEDIA_SECTION_IMAGES.platform.alt,
    features: [
      "Signal capture across CRM, market, and location data",
      "Map-driven context for places and communities",
      "Client Views as productized experiences",
      "Lead capture and opportunity routing",
      "Analytics, attribution, and reporting",
      "Client portals and operational workflows",
      "Data imports, enrichment, and integrations",
      "AI-assisted product and workflow strategy",
    ],
  },
  clientViews: {
    title: "Client Views",
    intro:
      "Each Client View is a focused, branded experience on the Midpoint platform — combining public pages, maps, CRM intake, analytics, and workflows for a specific market or business model.",
    cards: [
      {
        id: "golf-homes",
        name: "Golf Homes",
        status: "Live",
        audience: "Real estate buyers, agents, and golf community partners",
        description:
          "The first Client View on Midpoint: map-driven golf community search, property intelligence, CRM workflows, and market signals for golf course living. Available for private preview.",
        features: ["Map-based search", "CRM lead capture", "Property intelligence", "Partner workflows"],
        cta: { label: "Request a demo", href: "/contact?form=demo" },
        learnMoreHref: "/golf-homes",
      },
      {
        id: "casamapa",
        name: "CasaMapa",
        status: "Live",
        audience: "Buyers, sellers, agencies, and real estate partners in Colombia",
        description:
          "A live Spanish-language Client View for Colombia real estate — combining map-driven listing discovery, location intelligence, agency networks, and CRM-ready inquiries.",
        features: [
          "Map-based buy and rent search",
          "Agency directory and profiles",
          "Buyer accounts and compare",
          "CRM lead capture",
        ],
        cta: { label: "View CasaMapa", href: "https://casamapa.com.co" },
        learnMoreHref: "/case-studies/casamapa",
      },
      {
        id: "automap",
        name: "AutoMapa",
        status: "Live",
        audience: "Vehicle resellers, dealerships, and buyer leads",
        description:
          "Vehicle market Client View powered by Mobility inventory — search listings, surface deal signals, and generate CRM leads for dealers and resellers.",
        features: ["Vehicle inventory search", "Dealer lead capture", "Partnership intake", "Deal signals"],
        cta: { label: "View AutoMapa", href: "https://automapa.com.co" },
        learnMoreHref: "/case-studies/automap",
      },
      {
        id: "systemize-fit",
        name: "Systemize Fit",
        status: "Live",
        audience: "Trainers, gym operators, and coaching clients",
        description:
          "A live coaching operations product that brings programming, nutrition, scheduling, progress, and gym administration into one workspace for trainers, facilities, and clients.",
        features: [
          "Trainer programming and progress",
          "Gym and staff operations",
          "Client guidance and accountability",
          "Shared calendar and messaging",
        ],
        cta: { label: "View Systemize Fit", href: "https://systemizefit.com/" },
        learnMoreHref: "/case-studies/systemize-fit",
      },
      {
        id: "community-intelligence",
        name: "Community Intelligence",
        status: "In Development",
        audience: "Residents, relocators, and civic organizations",
        description:
          "Signal-rich community experience for neighborhoods, services, alerts, and local decision-making.",
        features: ["Community layers", "Alerts", "Local services", "Area intelligence"],
        cta: { label: "Learn More", href: "/community-intelligence" },
        learnMoreHref: "/case-studies/community-intelligence",
      },
      {
        id: "hvac-ecosystem",
        name: "HVAC Ecosystem Intelligence",
        status: "In Development",
        audience: "Distributors, manufacturers, recruiters, PE, and workforce agencies",
        description:
          "Map-centric industry intelligence connecting contractors, technicians, distributors, and training providers — with market scoring, coverage analysis, and canned reports. Available for private preview.",
        features: [
          "Ecosystem entity search",
          "Workforce & contractor density maps",
          "Distributor coverage analysis",
          "Canned market reports",
        ],
        cta: { label: "Request a demo", href: "/contact?form=demo" },
        learnMoreHref: "/case-studies/hvac-ecosystem",
      },
      {
        id: "land-homestead",
        name: "Land / Homestead Intelligence",
        status: "Concept",
        audience: "Land buyers, builders, and rural investors",
        description:
          "Evaluate rural land, zoning, water, risk, utilities, and buildability in one mapped Client View.",
        features: ["Parcel context", "Risk layers", "Utility visibility", "Buildability signals"],
        cta: { label: "Request Similar View", href: "/contact?form=demo" },
        learnMoreHref: "/case-studies/land-homestead",
      },
      {
        id: "local-business",
        name: "Local Business Map",
        status: "Concept",
        audience: "Chambers, business districts, economic development",
        description:
          "White-label directory and signal view for local businesses, districts, and regional growth.",
        features: ["Business directory", "District maps", "Sponsor listings", "Lead routing"],
        cta: { label: "Request Similar View", href: "/contact?form=demo" },
        learnMoreHref: "/case-studies/local-business",
      },
    ],
  },
  golfHomesCaseStudy: midpointMediaCaseStudyDefaults["golf-homes"],
  caseStudies: midpointMediaCaseStudyDefaults,
  platformDemo: {
    title: "How a Client View Works",
    intro: "The same signal-to-action flow powers Golf Homes and future Client Views.",
    steps: [
      { step: 1, title: "Arrive", body: "A user lands on a branded Client View for a specific market." },
      { step: 2, title: "Discover", body: "They explore maps, listings, or community signals." },
      { step: 3, title: "Inquire", body: "They submit a form or request more information." },
      { step: 4, title: "Capture", body: "The signal enters CRM with source attribution." },
      { step: 5, title: "Opportunity", body: "An opportunity is created and routed for follow-up." },
      { step: 6, title: "Measure", body: "Analytics tracks conversion by page, campaign, and view." },
      { step: 7, title: "Operate", body: "Teams manage content, workflows, and publishing in Studio." },
    ],
  },
  monetization: {
    title: "Ways We Help You Grow",
    pathways: [
      { title: "Consulting Projects", body: "Custom platform, data, CRM, and mapping work." },
      { title: "Client View Launch Packages", body: "Starter, professional, marketplace, and enterprise launches." },
      { title: "Sponsorship and Advertising", body: "Monetization for mapped community and real estate views." },
      { title: "Data Products", body: "Premium reports, dashboards, and intelligence datasets." },
      { title: "Platform Licensing", body: "Longer-term SaaS or managed-service offerings." },
    ],
  },
  leadCapture: {
    title: "Contact Midpoint Media",
    intro: "Start a conversation, request a platform walkthrough, or ask about building a Client View. Every submission creates a CRM record.",
    imageUrl: MIDPOINT_MEDIA_SECTION_IMAGES.contact.url,
    imageAlt: MIDPOINT_MEDIA_SECTION_IMAGES.contact.alt,
    inquiryTypes: [
      "General Contact",
      "Platform Demo",
      "Start a Project",
      "Client View Build Request",
      "Consulting",
      "Other",
    ],
  },
  insights: {
    title: "Insights",
    intro: "Articles and resources on signal intelligence, Client Views, mapping, CRM-driven products, and platform modernization.",
    comingSoon: true,
    topics: [
      "Signal intelligence",
      "Client View product design",
      "Real estate and community mapping",
      "Platform modernization",
      "CRM-driven websites",
      "Data and AI strategy",
    ],
  },
};

function normalizeNavLinks(links) {
  const base = midpointMediaSiteDefaults.navLinks;
  if (!Array.isArray(links) || !links.length) {
    return base;
  }
  const normalized = links
    .filter((link) => link.label !== "Golf Homes")
    .map((link) => {
      if (
        link.label === "Project Portal" ||
        link.href === "/login" ||
        link.href === "/client/login"
      ) {
        return { label: "Sign In", href: "/sign-in" };
      }
      return link;
    });
  const hasSignIn = normalized.some((link) => link.href === "/sign-in");
  if (!hasSignIn) {
    normalized.push({ label: "Sign In", href: "/sign-in" });
  }
  return normalized;
}

function normalizeHeroBadges() {
  return [...MIDPOINT_MEDIA_HERO_BADGES];
}

function omitViewGolfHomesCta(cta) {
  if (!cta || cta.label === "View Golf Homes") return undefined;
  return cta;
}

function mergeClientViewCards(overrideCards, baseCards) {
  if (!Array.isArray(overrideCards) || !overrideCards.length) {
    return baseCards;
  }
  const byId = new Map(overrideCards.map((card) => [card.id, card]));
  const merged = baseCards.map((card) => byId.get(card.id) || card);
  for (const card of overrideCards) {
    if (!baseCards.some((base) => base.id === card.id)) {
      merged.push(card);
    }
  }
  return merged;
}

export function mergeMidpointMediaSiteContent(content = {}) {
  const base = midpointMediaSiteDefaults;
  return {
    ...base,
    ...content,
    hero: {
      ...base.hero,
      ...content.hero,
      imageUrl: MIDPOINT_MEDIA_SECTION_IMAGES.hero.url,
      imageAlt: content.hero?.imageAlt || base.hero.imageAlt,
      badges: normalizeHeroBadges(),
      secondaryCta: omitViewGolfHomesCta(content.hero?.secondaryCta ?? base.hero.secondaryCta),
    },
    platformPositioning: {
      ...base.platformPositioning,
      ...content.platformPositioning,
      cards: content.platformPositioning?.cards || base.platformPositioning.cards,
    },
    clientViewsHome: {
      ...base.clientViewsHome,
      ...content.clientViewsHome,
      includes: content.clientViewsHome?.includes || base.clientViewsHome.includes,
    },
    platform: { ...base.platform, ...content.platform },
    clientViews: {
      ...base.clientViews,
      ...content.clientViews,
      cards: mergeClientViewCards(content.clientViews?.cards, base.clientViews.cards),
    },
    golfHomesCaseStudy: {
      ...base.golfHomesCaseStudy,
      ...content.golfHomesCaseStudy,
      cta: { ...base.golfHomesCaseStudy.cta, ...content.golfHomesCaseStudy?.cta },
    },
    caseStudies: mergeMidpointMediaCaseStudies(content.caseStudies),
    golfHomes: {
      ...base.golfHomes,
      ...content.golfHomes,
      imageUrl: MIDPOINT_MEDIA_SECTION_IMAGES.golfHomes.url,
      imageAlt: content.golfHomes?.imageAlt || base.golfHomes.imageAlt,
      cta: omitViewGolfHomesCta(content.golfHomes?.cta ?? base.golfHomes.cta),
    },
    consulting: {
      ...base.consulting,
      ...content.consulting,
      capabilities: content.consulting?.capabilities || base.consulting.capabilities,
      sections: content.consulting?.sections || base.consulting.sections,
      engagements: {
        ...base.consulting.engagements,
        ...content.consulting?.engagements,
        options: content.consulting?.engagements?.options || base.consulting.engagements.options,
      },
      credibility: { ...base.consulting.credibility, ...content.consulting?.credibility },
      closing: {
        ...base.consulting.closing,
        ...content.consulting?.closing,
        cta: { ...base.consulting.closing.cta, ...content.consulting?.closing?.cta },
      },
      primaryCta: { ...base.consulting.primaryCta, ...content.consulting?.primaryCta },
      secondaryCta: { ...base.consulting.secondaryCta, ...content.consulting?.secondaryCta },
    },
    differentiator: { ...base.differentiator, ...content.differentiator },
    homeCta: { ...base.homeCta, ...content.homeCta },
    platformDemo: {
      ...base.platformDemo,
      ...content.platformDemo,
      steps: content.platformDemo?.steps || base.platformDemo.steps,
    },
    monetization: {
      ...base.monetization,
      ...content.monetization,
      pathways: content.monetization?.pathways || base.monetization.pathways,
    },
    leadCapture: { ...base.leadCapture, ...content.leadCapture },
    insights: { ...base.insights, ...content.insights },
    clientPortal: {
      ...base.clientPortal,
      ...content.clientPortal,
      features: content.clientPortal?.features || base.clientPortal.features,
    },
    navLinks: normalizeNavLinks(content.navLinks || base.navLinks),
  };
}

/** @deprecated use mergeMidpointMediaSiteContent */
export const mergeMidpointMediaHomeContent = mergeMidpointMediaSiteContent;

export function isMidpointMediaLayout(page) {
  return page?.content?.pageLayout === "midpoint-media";
}

export function isMidpointMediaMarketingHome(page) {
  return page?.content?.homeLayout === MIDPOINT_MEDIA_MARKETING_HOME_LAYOUT;
}
